import * as React from 'react';
import style from './solution-badge.scss';
import { PremiumSmall, Premium, StatusWarningFilledSmall, StatusCompleteFilledSmall, } from '@wix/wix-ui-icons-common';
import { Box, Popover, Text } from '@wix/design-system';
import { composer } from '../../component-decorators/composer';
import { usePremiumCurrencyFormatter } from '@wix/premium-react-text';
import { getLowestPrice, getPricingLabel } from '../common';
export const SolutionBadge = composer()
    .withExperiments()
    .compose(({ t, isInstalled, showPremiumLabel, pricing, isBundleAppInstalled, isBundleAppNotInstalled, savedPrice, isAppSetupIncomplete, shouldShowIncompleteSetupPopover, experiments, companyId, textSize, textWeight, premiumTheme, isPreInstalled, }) => {
    const shouldShowFullPrices = experiments.enabled('specs.marketplace.fullPrices');
    const shouldShowNewBundleBadge = experiments.enabled('solution.page.freeBundleNotInstalled');
    const shouldMergeFreeBadges = false;
    const [isPopoverShown, setIsPopoverShown] = React.useState(false);
    const [seeFullPrices, setSeeFullPrices] = React.useState(false);
    React.useEffect(() => {
        if (shouldShowFullPrices &&
            pricing?.businessModel === 'PREMIUM' &&
            pricing?.freeTrialDays <= 0 &&
            pricing?.plans?.[0]?.premiumPrices?.fullPrices) {
            setSeeFullPrices(true);
        }
    }, [pricing?.plans?.[0]?.premiumPrices?.fullPrices]);
    const priceLabel = getPricingLabel(pricing, companyId);
    const [currencyToString] = usePremiumCurrencyFormatter({
        currencySettings: {
            [pricing?.currencySettings?.code]: {
                symbol: pricing?.currencySettings?.symbol,
                decimalSep: pricing?.currencySettings?.decimalSeparator,
                groupSep: pricing?.currencySettings?.groupSeparator,
                negPrefix: pricing?.currencySettings?.negativePrefix,
                negSuffix: pricing?.currencySettings?.negativeSuffix,
                posPrefix: '¤',
                posSuffix: '',
                fractionSize: 2,
            },
        },
        currencyCode: pricing?.currencySettings?.code,
    });
    const fixedPrice = savedPrice !== undefined && savedPrice !== 0
        ? Number(savedPrice).toFixed(2)
        : '';
    const fixedPriceWithTaxAndCurrency = shouldShowFullPrices
        ? currencyToString(Number(fixedPrice))
        : `$${fixedPrice}`;
    if (isPreInstalled) {
        return (React.createElement("span", { className: style.centeredView },
            React.createElement(StatusCompleteFilledSmall, { className: style.installedIcon }),
            React.createElement(Text, { size: "tiny", skin: "success", dataHook: "built-in-text" }, t('solutionBox.solutionBottomActionBar.builtInSolution'))));
    }
    if (isBundleAppNotInstalled && !shouldShowNewBundleBadge) {
        return (React.createElement("span", { className: style.flexView },
            fixedPrice !== '' && (React.createElement("span", { style: { textDecoration: 'line-through' } },
                React.createElement(Text, { size: textSize || 'tiny', weight: textWeight || 'thin' }, fixedPriceWithTaxAndCurrency))),
            React.createElement("span", { style: { marginLeft: '6px' } },
                React.createElement(Text, { size: textSize || 'tiny', weight: textWeight || 'thin', skin: "success" }, t('solutionBox.solutionBottomActionBar.BundleNotInstalled')))));
    }
    if (isBundleAppNotInstalled && shouldShowNewBundleBadge) {
        return (React.createElement("span", null,
            React.createElement(Text, { size: "tiny", weight: "thin", skin: "success" }, t('solution.page.freeBundleNotInstalled'))));
    }
    if (isBundleAppInstalled) {
        return (React.createElement("span", { className: style.bundleBadge },
            React.createElement(StatusCompleteFilledSmall, { className: style.installedIcon }),
            React.createElement(Text, { size: textSize || 'tiny', weight: textWeight || 'thin', skin: "success", dataHook: "installed-text" }, t('solutionBox.solutionBottomActionBar.BundleInstalled'))));
    }
    if (showPremiumLabel) {
        return (React.createElement("div", { className: style.premiumBadge },
            premiumTheme ? (React.createElement(Box, { color: "P10" }, textSize ? React.createElement(Premium, null) : React.createElement(PremiumSmall, null))) : (React.createElement(React.Fragment, null, textSize ? React.createElement(Premium, null) : React.createElement(PremiumSmall, null))),
            React.createElement(Text, { size: textSize || 'tiny', weight: textWeight || 'thin', skin: premiumTheme || 'standard', secondary: true, className: style.premiumText }, t('app.pricing.Premium'))));
    }
    if (isAppSetupIncomplete) {
        return (React.createElement(Popover, { showArrow: true, appendTo: "window", placement: "right", theme: "dark", shown: isPopoverShown, onMouseEnter: () => {
                if (shouldShowIncompleteSetupPopover) {
                    setIsPopoverShown(true);
                }
            }, onMouseLeave: () => {
                if (shouldShowIncompleteSetupPopover) {
                    setIsPopoverShown(false);
                }
            } },
            React.createElement(Popover.Element, null,
                React.createElement(Box, { verticalAlign: "middle" },
                    React.createElement(StatusWarningFilledSmall, { color: "#fdb10c" }),
                    React.createElement(Box, { marginLeft: "4px" },
                        React.createElement(Text, { skin: "standard", secondary: true, weight: textWeight || 'thin', size: textSize || 'tiny' }, t('solutionBox.solutionBottomActionBar.IncompleteSetup'))))),
            React.createElement(Popover.Content, null,
                React.createElement(Box, { padding: "12px 24px", maxWidth: "102px" },
                    React.createElement(Text, { skin: "standard", light: true, weight: textWeight || 'thin', size: textSize || 'small' }, t('solutionBox.solutionBottomActionBar.IncompleteSetupTooltip'))))));
    }
    if (isInstalled) {
        return (React.createElement("span", { className: style.centeredView },
            React.createElement(StatusCompleteFilledSmall, { className: style.installedIcon }),
            React.createElement(Text, { size: textSize || 'tiny', weight: textWeight || 'thin', skin: "success", dataHook: "installed-text" }, t('solutionBox.solutionBottomActionBar.installedSolution'))));
    }
    return (React.createElement(Text, { size: textSize || 'tiny', weight: textWeight || 'thin', skin: "standard", secondary: true, dataHook: "business-model-text", className: style.centeredView }, t(priceLabel, {
        trialDays: pricing?.freeTrialDays,
        price: pricing?.lowestMonthlyPrice,
        fullPrice: seeFullPrices
            ? currencyToString(getLowestPrice(pricing))
            : pricing?.lowestMonthlyPrice
                ? currencyToString(pricing?.lowestMonthlyPrice)
                : '$0',
    })));
});
//# sourceMappingURL=solution-badge.js.map